"use client";

import { saveSocialMedia } from "@faire/deprecated-retailer-visitor-shared/views/owf-small-business-grant/application-page/OwFGrantRequestData";
import {
  OwFGrantCtas,
  OwFGrantFlowPages,
} from "@faire/deprecated-retailer-visitor-shared/views/owf-small-business-grant/application-page/types";
import { SignupFormContainer } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_components/CommonStyles";
import { SignUpTitle } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_components/SignUpTitle";
import { useCreateUserAndRetailer } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_hooks/useCreateUserAndRetailer";
import {
  OwFGrantSocialMediaForm,
  useSchema,
} from "@faire/deprecated-retailer-visitor-shared/views/SignUp/owf-grant-social-media/__internal__/OwFGrantSocialMediaForm";
import { useSignUpRequest } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/SignUpRequestContext";
import { useSaveRequestAndGoToNextStep } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/signupStepper";
import { Step } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/Step";
import { Core } from "@faire/design-tokens";
import { trackOpenWithFaireOwfGrantPageCtaClicksClick } from "@faire/web-api--source/events/openWithFaire/click/owfGrantPageCtaClicks";
import { IRetailerLaunchWithFaireApplication } from "@faire/web-api--source/indigofair/data/IRetailerLaunchWithFaireApplication";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { Formik } from "formik";
import isUndefined from "lodash/isUndefined";
import React from "react";

export type SocialMediaData = Pick<
  IRetailerLaunchWithFaireApplication,
  "social_media"
> & {
  has_social_media: boolean | undefined;
};

export const OwFGrantSocialMediaStep: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const { saveAndGoToNextStep } = useSaveRequestAndGoToNextStep(
    Step.OWF_GRANT_SOCIAL_MEDIA
  );
  const [{ social_media }] = useSignUpRequest(["social_media"]);
  const schema = useSchema();

  const {
    createUserAndRetailer,
    userErrorMessage,
    isLoading: isCreatingUser,
  } = useCreateUserAndRetailer();

  const titleText = strictLocalize({
    id: "R8bQzK",
    defaultMessage: "Do you have a social media profile for your business?",
    description: {
      text: "Social media step in the retailer sign up flow",
      img: "https://cdn.faire.com/fastly/b587a4987cc97926476dd1dfd501e9cf4579d990b3f8fa61836eae3759899022.png",
    },
  });

  const onNext = async (values: SocialMediaData) => {
    const payload = { social_media: values.social_media };
    const response = await createUserAndRetailer(payload);

    if (response && !isUndefined(values?.has_social_media)) {
      saveSocialMedia(values.social_media);
      saveAndGoToNextStep(payload, { retailer: response.retailer });
      trackOpenWithFaireOwfGrantPageCtaClicksClick(
        OwFGrantCtas.NEXT,
        OwFGrantFlowPages.SIGN_UP_SOCIAL_MEDIA
      );
    }
  };

  return (
    <SignupFormContainer>
      <SignUpTitle title={titleText} />
      <Spacer height="4x" />
      <Formik
        initialValues={{ social_media, has_social_media: undefined }}
        validationSchema={schema}
        onSubmit={onNext}
      >
        <OwFGrantSocialMediaForm loading={isCreatingUser} />
      </Formik>
      {userErrorMessage ? (
        <>
          <Spacer height="2x" />
          <Typography color={Core.text.critical}>{userErrorMessage}</Typography>
        </>
      ) : null}
    </SignupFormContainer>
  );
};
